<template>
	<div
		class="event-market-drop-rate-box"
		:class="`event-market-drop-rate-box-${dropType}`"
	>
		<div
			class="event-market-drop-rate-box-value"
			:class="`drop-rate-${dropType}-${attributes.type}-50`"
		>
			{{ `${weight}%` }}
		</div>
		<div class="event-market-drop-rate-box-header">
			<BoxHeaderBackground
				slug="upgrade-box-header"
				:gradient="{
					stopColor1: '#339C51',
					stopColor2: '#1b4d29',
				}"
			/>
			<div class="event-market-drop-rate-box-header-text">
				{{ translate(`event.${dropType}_rewards`) }}
			</div>
		</div>
		<div class="event-market-drop-rate-box-rewards">
			<div
				class="event-market-drop-rate-box-rewards-item"
				v-for="(reward, index) in rewards"
				:key="index"
			>
				<Tooltip
					v-if="
						toGrandPrizeInGameReward(reward, resourcesStore)
							?.type === GrandPrizeRewardType.CURRENCY &&
						isResourceUnlocked(reward)
					"
					:placement="Placements.TOP"
				>
					<template #element>
						<AppPrice
							:amount="
								toGrandPrizeInGameReward(reward, resourcesStore)
									?.amount ?? 0
							"
							:currency="
								(
									toGrandPrizeInGameReward(
										reward,
										resourcesStore,
									) as GrandPrizeInGameRewardCurrency
								)?.currency
							"
							:resourceCheck="false"
							:iconSize="20"
							:fontSize="24"
						/>
					</template>
					<template #tooltip>
						<span
							v-if="
								(
									toGrandPrizeInGameReward(
										reward,
										resourcesStore,
									) as GrandPrizeInGameRewardCurrency
								)?.currency === ResourceType.CHEST_KEY
							"
							>{{ translate("event.legendary_chest_key") }}</span
						>
						<span v-else>
							{{
								translate(
									`resource.${
										(
											toGrandPrizeInGameReward(
												reward,
												resourcesStore,
											) as GrandPrizeInGameRewardCurrency
										)?.currency
									}`,
								)
							}}
						</span>
					</template>
				</Tooltip>

				<Tooltip
					v-else-if="
						toGrandPrizeInGameReward(reward, resourcesStore)
							?.type === GrandPrizeRewardType.CURRENCY
					"
					:heading="
						translate(
							`resource.${
								(
									toGrandPrizeInGameReward(
										reward,
										resourcesStore,
									) as GrandPrizeInGameRewardCurrency
								)?.currency
							}`,
						)
					"
					customWidth="18rem"
					:placement="Placements.TOP"
				>
					<template #element>
						<div class="resource-locked">
							<div class="resource-locked__value">
								{{ reward.amount }}
							</div>
							<div class="icon-lock" />
						</div>
					</template>
					<template #tooltip>
						<span class="resource-locked__content">
							{{ translate("event.reward_locked") }}
						</span>
					</template>
				</Tooltip>
				<Tooltip v-else customWidth="18rem" :placement="Placements.TOP">
					<template #element>
						<AppItemAmount
							:amount="reward.amount as number"
							:frameName="
								(
									toGrandPrizeInGameReward(
										reward,
										resourcesStore,
									) as GrandPrizeInGameRewardItem
								)?.frameName
							"
							:iconSize="20"
							:fontSize="24"
						/>
					</template>
					<template #tooltip>
						<span v-html="buildingTooltip(reward)" />
					</template>
				</Tooltip>
			</div>
		</div>
		<div
			class="event-market-drop-rate-box-footer"
			v-if="dropType === MarketDropRateType.RARE"
		>
			<div>
				<div class="limit-text">
					<span
						>{{
							`${translate("event.daily_gem_limit")}: ${grandPrizeState?.gemsEarnedToday}/${attributes.grand_prizes?.dailyGemLimit}`
						}}
					</span>
					<Tooltip customWidth="18rem" :placement="Placements.TOP">
						<template #element>
							<AppIconButton
								class="income-box__info"
								:type="IconTypes.INFO"
								:size="12"
								:iconSize="9"
							/>
						</template>
						<template #tooltip>
							<span
								class="event-market-drop-rate-box-footer__tooltip"
							>
								{{
									translate(
										"event.reward_unavailable_daily_limit",
									)
								}}
							</span>
						</template>
					</Tooltip>
				</div>
				<div class="limit-text">
					<span>
						{{
							`${translate("event.daily_keys_limit")}: ${grandPrizeState?.chestKeysEarnedToday}/${attributes.grand_prizes?.dailyChestKeyLimit}`
						}}
					</span>
					<Tooltip customWidth="18rem" :placement="Placements.TOP">
						<template #element>
							<AppIconButton
								class="income-box__info"
								:type="IconTypes.INFO"
								:size="12"
								:iconSize="9"
							/>
						</template>
						<template #tooltip>
							<span
								class="event-market-drop-rate-box-footer__tooltip"
							>
								{{
									translate(
										"event.reward_unavailable_daily_limit",
									)
								}}
							</span>
						</template>
					</Tooltip>
				</div>
			</div>
		</div>

		<div
			class="event-market-drop-rate-box-decoration"
			v-if="dropType === MarketDropRateType.RARE"
		>
			<GoldenCorner top right />
			<GoldenCorner bottom left />
			<GoldenCorner bottom right />
			<div class="image-decor" />
		</div>
	</div>
</template>
type
<script lang="ts" setup>
import type { GrandPrizeInGameRewardItem } from "@/event/index.ts";
import {
	GrandPrizeRewardType,
	toGrandPrizeInGameReward,
	useEventStore,
	type GrandPrizeInGameRewardCurrency,
} from "@/event/index.ts";
import { FrameName } from "@/game/entities/config/index.ts";
import { ResourceType } from "@/game/entities/config/resources.ts";
import { getUnlocksByLevel } from "@/game/utils/resources.ts";
import useSettlementsStore from "@/store/useSettlementsStore.ts";
import { MarketDropRateType } from "@/types/event";
import type { EventMilestoneRewardConfig } from "@/types/index.ts";
import { IconTypes, Placements } from "@/types/ui.ts";
import { replacePlaceholder } from "@/utils/replacePlaceholder.ts";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import useResourcesStore from "../../store/useResourcesStore.js";
import AppIconButton from "../AppIconButton.vue";
import AppItemAmount from "../AppItemAmount.vue";
import AppPrice from "../AppPrice.vue";
import BoxHeaderBackground from "../SVG/BoxHeaderBackground.vue";
import GoldenCorner from "../SVG/GoldenCorner.vue";
import Tooltip from "../Tooltip.vue";

const eventStore = useEventStore();
const { attributes, grandPrizeState } = storeToRefs(eventStore);

const resourcesStore = useResourcesStore();

const unlockByLevel = getUnlocksByLevel();

const settlementStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementStore);

interface Props {
	rewards: EventMilestoneRewardConfig[];
	weight: number;
	dropType: MarketDropRateType;
}

const { rewards, weight, dropType } = defineProps<Props>();

const resourcesUnlocks = computed<Partial<Record<ResourceType, boolean>>>(
	(): Partial<Record<ResourceType, boolean>> => {
		const result = {} as Partial<Record<ResourceType, boolean>>;
		(
			Object.entries(unlockByLevel) as Array<[ResourceType, number]>
		).forEach(([key, value]: [ResourceType, number]): void => {
			result[key] = value <= mainLevel.value;
		});
		return result;
	},
);

const isResourceUnlocked = (reward: EventMilestoneRewardConfig): boolean => {
	const inGameReward = toGrandPrizeInGameReward(reward, resourcesStore);

	if (inGameReward?.type !== GrandPrizeRewardType.CURRENCY) {
		return false;
	}

	return (
		resourcesUnlocks.value[inGameReward.currency] === true ||
		resourcesUnlocks.value[inGameReward.currency] === undefined
	);
};

const buildingTooltip = (reward: EventMilestoneRewardConfig): string => {
	const mutatedReward = toGrandPrizeInGameReward(
		reward,
		resourcesStore,
	) as GrandPrizeInGameRewardItem;

	if (mutatedReward.frameName === FrameName.TOYMAKERS_WORKSHOP) {
		return replacePlaceholder(
			replacePlaceholder(
				replacePlaceholder(
					translate(`${FrameName.TOYMAKERS_WORKSHOP}.tooltip`),
					"{number1}",
					500,
				),
				"{number2}",
				100,
			),
			"{number3}",
			100,
		);
	}
	if (
		[
			FrameName.RABBIT_FARM,
			FrameName.RAFT,
			FrameName.BOTTLETRAP,
			FrameName.FORTRESS,
		].includes(mutatedReward.frameName)
	) {
		return translate(`${mutatedReward.frameName}.description`);
	}
	return translate(`${mutatedReward.frameName}.name`);
};
</script>

<style lang="scss" scoped>
.event-market-drop-rate-box {
	@include flex-col-start;
	position: relative;
	border-width: 0.05rem;
	border-style: solid;
	border-image-source: linear-gradient(
		0deg,
		rgba(189, 144, 50, 1) 0%,
		rgba(240, 211, 134, 1) 100%
	);
	border-image-slice: 1;
	width: 100%;
	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 1 !important;
		// NOTE: Multiply blending is better for light colors
		mix-blend-mode: multiply !important;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat left;
		background-size: 80%;
		width: 100%;
		height: 100%;
		content: "";
	}
	&-value {
		@include flex-row-center;
		position: absolute;
		top: -0.5rem;
		left: -0.5rem;
		z-index: 1;
		padding-bottom: 0.25rem;
		color: #f2dcb5;
		font-weight: bold;
		font-size: 0.906rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}
	&-common {
		background-image: linear-gradient(
			0deg,
			rgba(75, 107, 60, 1) 0%,
			rgba(154, 194, 134, 1) 100%
		);
		:deep(.box-header-bg) {
			.texture-bg-polygon {
				fill: rgba($color: #136329, $alpha: 0.5) !important;
			}
		}
	}
	&-uncommon {
		background-image: linear-gradient(
			0deg,
			rgba(60, 82, 117, 1) 0%,
			rgba(140, 165, 205, 1) 100%
		);
		:deep(.box-header-bg) {
			.texture-bg-polygon {
				fill: rgba($color: #193e79, $alpha: 0.5) !important;
			}
		}
	}
	&-rare {
		background-image: linear-gradient(
			0deg,
			rgba(102, 21, 125, 1) 0%,
			rgba(218, 91, 255, 1) 100%
		);
		:deep(.box-header-bg) {
			.texture-bg-polygon {
				fill: rgba($color: #771f91, $alpha: 0.5) !important;
			}
		}
	}
	&-header {
		position: relative;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
		&-text {
			position: absolute;
			top: 0;
			left: 3.5rem;
			line-height: 1.5;
		}
	}
	&-rewards {
		@include flex-row-center;
		flex-wrap: wrap;
		margin: 0.5rem 0 1rem 0;
		padding: 0 1rem;
		width: 100%;
		&-item {
			z-index: 1;
			padding: 0 0.5rem;
		}
	}
	&-footer {
		background: rgba($color: #23002d, $alpha: 0.6);
		padding: 0.15rem 0;
		@include flex-row-center;
		gap: 0.15rem;
		width: 100%;
		color: #fff3dd;
		font-size: 0.813rem;
		&__tooltip {
			text-align: center;
		}
		.limit-text {
			@include flex-row-center;
			gap: 0.15rem;
		}
	}
	&-decoration {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 100%;
		pointer-events: none;

		:deep(.golden-corner) {
			width: 1.5rem;
			height: 1.5rem;
		}

		:deep(.golden-corner--top-right) {
			position: absolute;
			top: 0;
			right: 0;
			z-index: 1;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
	}
	.resource-locked {
		@include flex-row-center;
		gap: 0.25rem;
		line-height: 1.5;

		&__value {
			opacity: 0.4;
			color: #fff3dd;
			font-weight: bold;
			font-size: 1.5rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
		&__content {
			text-align: center;
		}
		.icon-lock {
			width: 1.25rem;
			height: 1.25rem;
		}
	}
}
</style>
